import { Platform, View } from 'react-native';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  Footer,
  Input,
  KeyboardScreenContainer,
  Select,
  Text,
} from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import HasInsuranceModal from './components/HasInsuranceModal';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import type { MarisaAutoStepsProps } from '../..';
import type { ManufacturerEntity } from '~/domain/Insurance/manufacturerEntity';
import type { VehicleModelEntity } from '~/domain/Insurance/vehicleModelEntity';
import useToast from '~/hooks/useToast';
import { getManufacturersUseCase } from '~/useCases/AssegurouUseCases/GetManufacturersUseCase';
import { getVehicleModelsByYearManufacturerUseCase } from '~/useCases/AssegurouUseCases/GetVehicleModelsByYearManufacturerUseCase';
import { useSalesFunnel } from '~/stores';

const schema = yup
  .object()
  .shape({
    brand: yup.string().required('A fabricante do veículo é obrigatória'),
    year: yup.number().min(4, 'Ano inválido').required('Ano obrigatório'),
    modelYear: yup.number().min(4, 'Ano inválido').required('Ano obrigatório'),
    model: yup.string().required('O modelo do veículo é obrigatório'),
    // version: yup.string().required('A versão do veículo é obrigatória'),
  })
  .required();

interface VehicleForm {
  brand: string;
  year: number;
  modelYear: number;
  model: string;
  version: string;
  modelDescription: string;
  brandDescription: string;
}

const Vehicle = ({ onContinue, goToStep }: MarisaAutoStepsProps) => {
  const [manufacturerEntity, setManufacturerEntity] =
    useState<ManufacturerEntity>({
      success: false,
      manufacturers: [],
    });
  const [vehicleModelEntity, setVehicleModelEntity] =
    useState<VehicleModelEntity>({
      success: false,
      vehicleModels: [],
    });
  const [showHasInsuranceModal, setShowHasInsuranceModal] =
    useState<boolean>(false);
  const [loadingManufacturers, setLoadingManufacturers] =
    useState<boolean>(false);
  const [loadingVehicleModels, setLoadingVehicleModels] =
    useState<boolean>(false);

  const { showError } = useToast();
  const { setInfos } = useSalesFunnel();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VehicleForm>({
    resolver: yupResolver(schema),
  });

  const manufacturer = watch('brand');
  const modelYear = watch('modelYear');

  const { setVehicle } = useMarisaAutoForm();

  const onSubmit = useCallback(
    async (data: VehicleForm) => {
      try {
        await setVehicle(data);
        setShowHasInsuranceModal(true);
      } catch (e: any) {
        if (e?.message) {
          showError({ title: e.message, description: '' });
        }
      }
    },
    [setVehicle, showError]
  );

  useEffect(() => {
    setLoadingManufacturers(true);

    async function getManufacturers(): Promise<ManufacturerEntity> {
      return await getManufacturersUseCase();
    }

    getManufacturers()
      .then((result) => {
        setManufacturerEntity(result);
      })
      .finally(() => {
        setLoadingManufacturers(false);
      });
  }, []);

  useEffect(() => {
    if (manufacturer) {
      for (const element of manufacturerEntity.manufacturers) {
        if (element.value === manufacturer) {
          useMarisaAutoForm.getState().setBrandDescription(element.label);
        }
      }
    }

    if (modelYear && modelYear.toString().length === 4 && manufacturer) {
      setLoadingVehicleModels(true);

      async function getVehicleModelsByManufacturerYear(): Promise<VehicleModelEntity> {
        return await getVehicleModelsByYearManufacturerUseCase(
          manufacturer,
          modelYear.toString()
        );
      }

      getVehicleModelsByManufacturerYear()
        .then((result) => {
          setVehicleModelEntity(result);
        })
        .finally(() => {
          setLoadingVehicleModels(false);
        });
    }
  }, [manufacturer, manufacturerEntity.manufacturers, modelYear]);

  return (
    <KeyboardScreenContainer
      footer={
        <View
          className={`flex xl:hidden ${
            Platform.OS === 'web' ? 'py-4 px-6' : ''
          }`}
        >
          <Footer disabled={isSubmitting} onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24 mb-4">
          <Text className="text-xl mb-4 text-gray-600">
            Qual o modelo do seu carro?
          </Text>

          <Text className="text-gray-400 text-sm mb-8">
            Saber qual é o modelo do seu carro é essencial para lhe mostrarmos
            os planos e preços disponíveis corretamente.
          </Text>

          <Select
            type="search"
            searchPlaceholder="Digite aqui o fabricante do veículo"
            name="brand"
            label="Fabricante*"
            placeholder="Selecione o fabricante do veículo"
            control={control}
            error={errors.brand?.message}
            items={manufacturerEntity.manufacturers}
            loading={loadingManufacturers}
          />

          <View className="flex-row">
            <Input
              control={control}
              name="year"
              label="Ano Fabricação"
              placeholder="Ex.: 2014"
              maskConfig={{ type: 'only-numbers' }}
              maxLength={4}
              className="flex-1 mr-2"
              keyboardType="decimal-pad"
              error={errors.year?.message}
            />
            <Input
              control={control}
              name="modelYear"
              label="Ano Modelo"
              placeholder="Ex.: 2015"
              maskConfig={{ type: 'only-numbers' }}
              maxLength={4}
              className="flex-1"
              keyboardType="decimal-pad"
              error={errors.modelYear?.message}
            />
          </View>

          <Select
            disabled={
              vehicleModelEntity.success === false ||
              vehicleModelEntity.vehicleModels.length === 0
            }
            type="search"
            searchPlaceholder="Digite aqui o modelo do veículo"
            name="model"
            label="Qual o modelo do veículo?"
            placeholder="Selecione o modelo do veículo"
            control={control}
            error={errors.model?.message}
            items={vehicleModelEntity.vehicleModels}
            loading={loadingVehicleModels}
          />

          {/*<Select*/}
          {/*  disabled={!model}*/}
          {/*  name="version"*/}
          {/*  label="Qual a versão do seu veículo?"*/}
          {/*  placeholder="Selecione a versão do veículo"*/}
          {/*  control={control}*/}
          {/*  error={errors.version?.message}*/}
          {/*  items={MOCK_MODELS}*/}
          {/*/>*/}

          <Footer
            className="hidden"
            disabled={isSubmitting}
            onSubmit={handleSubmit(onSubmit)}
          />

          <HasInsuranceModal
            visible={showHasInsuranceModal}
            onClose={(type) => {
              setShowHasInsuranceModal(false);

              if (type && type === 'no' && goToStep) {
                setInfos({ hasInsurance: false });
                setTimeout(() => goToStep(4), 350);
              } else {
                setInfos({ hasInsurance: true });
                setTimeout(() => onContinue(), 350);
              }
            }}
          />
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default Vehicle;
